/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { SelectField, SelectOption } from '@coursera/cds-core';

import { LEARNING_GOAL_OPTIONS } from 'bundles/onboarding-common/constants';
import type { LEARNING_GOAL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateLearningGoal from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateLearningGoal';
import { getLearningGoalOptionName } from 'bundles/onboarding-common/utils';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import { useTrackPersonalizationSelectChange } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';

import _t from 'i18n!nls/personalization-settings';

// CDS SelectField's `fullWidth` only widens the outer FormControl, not the
// input box itself, leaving the visible control shrunk to its content width
// instead of filling the row's second column. Force it via CDS's own public
// class hook (see EducationLevelSettingItem for the same workaround).
const styles = {
  select: css`
    min-width: 0;

    .cds-select-field-root {
      width: 100%;
      min-width: 0;
    }
  `,
};

const LABEL_ID = 'learning-goal-label';

export type LearningGoalSettingItemProps = {
  label: string;
  /** Called after the learner's selection is saved as "Grow in my current role". */
  onSelectImproveSkills?: () => void;
};

const LEARNING_GOAL_OPTION_IDS = Object.values(LEARNING_GOAL_OPTIONS);
const LEARNING_GOAL_OPTION_ID_SET = new Set<string>(LEARNING_GOAL_OPTION_IDS);

const isLearningGoalOption = (value: string): value is LEARNING_GOAL_OPTIONS_TYPE =>
  LEARNING_GOAL_OPTION_ID_SET.has(value);

const LearningGoalSettingItem = ({ label, onSelectImproveSkills }: LearningGoalSettingItemProps) => {
  const { values, loading, setFieldValue } = usePersonalizationSettingsContext();
  const { updateLearningGoal } = useLearnerObjectUpdateLearningGoal();
  const optionNames = getLearningGoalOptionName();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  const {
    value: selectedValue,
    notificationType,
    onChange: handleSelectionChange,
  } = useSettingSave(
    values.learningGoal,
    async (value) => {
      if (!value) return { success: true, errors: [] };
      const result = await updateLearningGoal(value);
      if (result.success) {
        setFieldValue('learningGoal', value);
      }
      return result;
    },
    'learningGoal'
  );

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value: nextValue } = event.target;
    if (typeof nextValue === 'string' && isLearningGoalOption(nextValue)) {
      handleSelectionChange(nextValue);
      trackSelectChange('learning_goal', [nextValue]);
      if (nextValue === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS) {
        onSelectImproveSkills?.();
      }
    }
  };

  return (
    <SettingRow label={label} labelId={LABEL_ID} notificationType={notificationType} loading={loading}>
      <SelectField
        id="learning-goal-select"
        data-testid="learning-goal-select"
        aria-labelledby={LABEL_ID}
        css={styles.select}
        placeholder={_t('Select an option')}
        value={selectedValue ?? ''}
        onChange={handleChange}
        fullWidth
      >
        {LEARNING_GOAL_OPTION_IDS.map((optionId) => (
          <SelectOption key={optionId} value={optionId}>
            {optionNames[optionId]}
          </SelectOption>
        ))}
      </SelectField>
    </SettingRow>
  );
};

export default LearningGoalSettingItem;
